/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { usePortalSession } from '../../context/PortalContext';
import axiosClient from '../../api/axiosClient';
import NotificationCenter from './NotificationCenter';
import LanguageSwitcher from '../LanguageSwitcher';

const PortalTopbar = ({ onMenuToggle, isSidebarOpen }) => {
  const { t } = useTranslation('booking');
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { portalData } = usePortalSession();
  const location = useLocation();
  const hasCustomLogo = !!portalData?.brandingLogo?.url;
  const logoSrc = hasCustomLogo
    ? `${portalData.brandingLogo.url}${portalData.brandingLogo.version ? `?v=${portalData.brandingLogo.version}` : ''}`
    : '/images/logos/Hairchitech_Logo1.png';
  
  // Profile dropdown state (client)
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
    };
    const onEscape = (e) => e.key === 'Escape' && setIsProfileOpen(false);
    if (isProfileOpen) {
      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('keydown', onEscape);
    }
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isProfileOpen]);

  const handleLogout = () => {
    logout();
    window.location.href = '/?auth=1';
  };

  const goProfile = () => {
    window.location.href = '/app/profile';
  };

  return (
    <header 
      className={`fixed left-0 right-0 z-[150] h-24 transition-all duration-500 ease-out overflow-hidden admin-topbar ${isDark ? 'theme-dark' : 'theme-light'} max-w-full`}
      style={{ top: '0px' }}
      onClick={(e) => {
        // Remove stopPropagation to allow button clicks to work properly
      }}
    >
      
        {/* Content Container */}
        <div className="relative z-10 flex items-center gap-2 px-3 h-24 max-w-full">
        {/* Left: mobile menu toggle */}
        <motion.button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMenuToggle();
          }}
          className={`mr-1 p-2 admin-icon-button clickable-item md:hidden ${isDark ? 'theme-dark' : 'theme-light'}`}
          style={{
            color: isDark ? '#e7ebef' : '#5a5a5a'
          }}
          aria-label="Toggle menu"
          animate={{
            rotate: isSidebarOpen ? 90 : 0,
            scale: isSidebarOpen ? 1.2 : 1,
          }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: '28px', lineHeight: '28px', display: 'block' }}>✂️</span>
        </motion.button>

        {/* Search Bar - on the left */}
        <div className="flex-1 max-w-md relative z-50 min-w-0">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search bookings, services, appointments..."
              className={`w-full pl-10 pr-4 py-3 admin-search clickable-item ${isDark ? 'theme-dark' : 'theme-light'} min-w-0`}
              style={{
                background: isDark ? '#2a2f36' : '#f9f9f9',
                border: isDark ? '1px solid #383e45' : '1px solid #dddddd',
                color: isDark ? '#e7ebef' : 'var(--text)',
                borderRadius: '12px',
                boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,.06)'
              }}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <button
                className="p-1 rounded clickable-item"
                style={{
                  color: isDark ? '#98a0a8' : '#5a5a5a'
                }}
                aria-label="Search"
              >
                <svg 
                  className="h-5 w-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Right: Logo */}
        <div className="flex items-center mr-4">
          <div className="relative">
            <Link to="/">
              <img
                src={logoSrc}
                alt="HairChiTech"
                // Logged-in client portal topbar: bigger, but still fits within the 96px header.
                className={`cursor-pointer shrink-0 w-auto object-contain h-14 sm:h-16 md:h-18 ${isDark ? 'admin-logo-dark' : 'admin-logo-light'}`}
              />
            </Link>
          </div>
        </div>

                {/* Right: actions – keep on one line, never wrap */}
                <div className="ml-auto flex items-center gap-2 shrink-0">
                  {/* Notification Center */}
                  <NotificationCenter />
                  
                  {/* Theme Toggle */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleTheme();
                    }}
                    className={`p-2 admin-icon-button clickable-item ${isDark ? 'theme-dark' : 'theme-light'}`}
                    style={{
                      color: isDark ? '#e7ebef' : '#5a5a5a'
                    }}
                    aria-label="Theme"
                    title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                  >
                    {isDark ? (
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" 
                        />
                      </svg>
                    ) : (
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" 
                        />
                      </svg>
                    )}
                  </button>
          
          <span 
            className="hidden md:inline-flex px-3 py-1 text-xs rounded-full font-medium clickable-item"
            style={{
              background: isDark ? 'rgba(125,102,51,.18)' : 'var(--gold-300)',
              color: isDark ? '#f0d9a6' : 'var(--gold-600)',
              border: isDark ? '1px solid rgba(125,102,51,.3)' : '1px solid var(--gold-400)'
            }}
          >
            {t('booking.portal_user_role')}
          </span>
          
          {/* Language switcher — visible on all devices */}
          <div className="flex-shrink-0">
            <LanguageSwitcher />
          </div>

          {/* Profile dropdown trigger */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setIsProfileOpen((v) => !v)}
              className="p-1.5 rounded-full hover:bg-accent clickable-item"
              aria-label="Profile"
              aria-expanded={isProfileOpen}
            >
              <div className="h-8 w-8 rounded-full flex items-center justify-center text-white font-semibold shadow-lg" style={{ backgroundColor: '#7d6633' }}>
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            </button>

            {isProfileOpen && (
              <div
                className={`fixed w-64 rounded-lg border shadow-lg z-[200] ${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}
                style={{ top: '100px', right: '16px' }}
              >
                <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold" style={{ backgroundColor: '#7d6633' }}>
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.name || user?.email || 'User'}</p>
                      <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Client</p>
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <button
                    onClick={goProfile}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                      isDark ? 'text-gray-300 hover:bg-gray-700 hover:text-white' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>{t('booking.portal_nav_profile')}</span>
                    </div>
                  </button>

                  <div className={`${isDark ? 'border-gray-700' : 'border-gray-200'} border-t`}></div>

                  <button
                    onClick={handleLogout}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                      isDark ? 'text-gray-300 hover:bg-gray-700 hover:text-white' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h5a2 2 0 012 2v1" />
                      </svg>
                      <span>{t('booking.portal_logout')}</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const PortalSidebar = ({ isOpen, onClose }) => {
  const { t } = useTranslation('booking');
  const { isDark } = useTheme();
  const location = useLocation();

  const navItems = [
    { path: '/app/book', label: t('booking.portal_nav_book'), icon: 'calendar' },
    { path: '/app/bookings', label: t('booking.portal_nav_bookings'), icon: 'list' },
    { path: '/app/profile', label: t('booking.portal_nav_profile'), icon: 'user' }
  ];

  // Icon component to render SVG icons
  const IconComponent = ({ iconName, isActive = false }) => {
    const iconProps = {
      className: `h-5 w-5`,
      fill: "none",
      stroke: "currentColor",
      viewBox: "0 0 24 24",
        style: {
          color: isActive 
            ? (isDark ? '#f0d9a6' : 'var(--gold-600)')
            : (isDark ? '#d2d6db' : '#4a4a4a')
        }
    };

    switch (iconName) {
      case 'calendar':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'list':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case 'user':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={(e) => {
            onClose();
          }}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-24 left-0 h-[calc(100vh-6rem)] z-40 transition-all duration-500 ease-out overflow-hidden max-w-full ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0 admin-sidebar ${isDark ? 'theme-dark' : 'theme-light'}`}>
        
        {/* Content Container */}
        <div className="relative z-10 w-64 h-full" style={{ color: isDark ? 'var(--text)' : 'var(--text)' }}>
          {/* Panel Header - Integrated into sidebar */}
          <div className="p-4 border-b" style={{ borderColor: isDark ? 'var(--panel-border)' : 'var(--panel-border)' }}>
            <div className="flex items-center justify-center">
              <div className="relative">
                <p className="text-lg font-semibold text-center admin-section-title">
                  {t('booking.portal_client')}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4">
            <ul className="space-y-4">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `
                      flex items-center space-x-3 admin-nav-item clickable-item
                      ${isActive ? 'active nav-item-active-entrance' : ''}
                      ${isDark ? 'theme-dark' : 'theme-light'}
                    `}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                    }}
                  >
                    {({ isActive }) => (
                      <>
                        <IconComponent iconName={item.icon} isActive={isActive} />
                        <span className="font-medium" style={{
                          color: isActive 
                            ? (isDark ? '#f0d9a6' : 'var(--gold-600)')
                            : (isDark ? 'var(--text)' : 'var(--text)')
                        }}>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

const PortalBottomNav = () => {
  const { t } = useTranslation('booking');
  const { isDark } = useTheme();
  const location = useLocation();

  const navItems = [
    { path: '/app/book', label: t('booking.portal_nav_book'), icon: 'calendar' },
    { path: '/app/bookings', label: t('booking.portal_nav_bookings'), icon: 'list' },
    { path: '/app/profile', label: t('booking.portal_nav_profile'), icon: 'user' }
  ];

  // Icon component to render SVG icons
  const IconComponent = ({ iconName }) => {
    const iconProps = {
      className: "h-6 w-6",
      fill: "none",
      stroke: "currentColor",
      viewBox: "0 0 24 24"
    };

    switch (iconName) {
      case 'calendar':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'list':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case 'user':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-40 border-t bg-background md:hidden ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } pb-[calc(env(safe-area-inset-bottom,0px)+8px)] max-w-full overflow-x-hidden`}
      role="navigation"
    >
      <div
        className="flex gap-4 px-4 py-2 overflow-x-auto no-scrollbar snap-x snap-mandatory justify-center max-w-full"
      >
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center min-w-[70px] snap-start px-2 py-1 text-xs transition-colors duration-200 ${
              location.pathname === item.path
                ? isDark
                  ? 'text-blue-400'
                  : 'text-blue-600'
                : isDark
                ? 'text-gray-400 hover:text-gray-300'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <IconComponent iconName={item.icon} />
            <span className="mt-0.5">{item.label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
};

const PortalLayout = ({ children }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const { portalData } = usePortalSession();
  
  // Check if this is a public booking (no authentication required)
  const [isPublicBooking, setIsPublicBooking] = useState(false);
  const [publicLogo, setPublicLogo] = useState({ url: null, version: null });
  
  useEffect(() => {
    // Check for public booking mode
    const checkPublicBooking = () => {
      try {
        const publicBookingId = localStorage.getItem('public_booking_id');
        const onBookingPath = location.pathname.startsWith('/app/book');
        // If a public booking id is present, force public mode even if user is logged in.
        const isPublic = !!publicBookingId && onBookingPath;
        setIsPublicBooking(isPublic);
        console.log('[PortalLayout] Public booking mode:', isPublic, 'publicBookingId:', publicBookingId, 'user:', !!user);
      } catch (err) {
        setIsPublicBooking(false);
      }
    };
    
    checkPublicBooking();
  }, [user, location.pathname]);

  // For public booking flows, load the salon logo from the public booking endpoint.
  useEffect(() => {
    const loadPublicLogo = async () => {
      if (!isPublicBooking) return;
      let publicBookingId = null;
      try {
        publicBookingId = localStorage.getItem('public_booking_id');
      } catch (_) {
        publicBookingId = null;
      }
      if (!publicBookingId) return;
      try {
        const resp = await axiosClient.get(`/public/booking/${publicBookingId}`);
        const url = resp.data?.salon?.logo || null;
        const version = resp.data?.salon?.logo_version ?? null;
        setPublicLogo({ url, version });
      } catch (_) {
        // Non-blocking fallback to default logo
        setPublicLogo({ url: null, version: null });
      }
    };
    loadPublicLogo();
  }, [isPublicBooking]);

  const defaultLogo = '/images/logos/Hairchitech_Logo1.png';
  const clientLogoSrc = portalData?.brandingLogo?.url
    ? `${portalData.brandingLogo.url}${portalData.brandingLogo.version ? `?v=${portalData.brandingLogo.version}` : ''}`
    : defaultLogo;
  const publicLogoSrc = publicLogo?.url
    ? `${publicLogo.url}${publicLogo.version ? `?v=${publicLogo.version}` : ''}`
    : defaultLogo;

  // SIMPLE PUBLIC BOOKING LAYOUT (No navigation, no profile, no admin links)
  if (isPublicBooking) {
    return (
      <div className="min-h-screen transition-all duration-500 ease-out relative max-w-full overflow-x-hidden">
        {/* Background */}
        {isDark ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800 pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent pointer-events-none"></div>
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-gray-100 pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-amber-100/30 via-yellow-50/20 to-transparent pointer-events-none"></div>
          </>
        )}
        
        {/* Simple Header with Logo Only */}
        <div className="relative z-10">
          <div className="flex items-center justify-center py-4 sm:py-5 md:py-6">
            <img
              src={publicLogoSrc}
              alt="HairChiTech"
              // Public booking header (Book Your Appointment page): this should feel like the old 120px logo.
              className="cursor-pointer w-auto object-contain h-20 sm:h-24 md:h-28"
            />
          </div>
        </div>
        
        {/* Content */}
        <main className={`relative z-10 ${isDark ? 'text-white' : 'text-gray-900'} px-4 md:px-6 lg:px-8 pb-8`}>
          <div className="max-w-4xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    );
  }

  // AUTHENTICATED CLIENT LAYOUT (Full navigation)
  return (
    <div className="h-screen transition-all duration-500 ease-out relative max-w-full overflow-x-hidden">
      {/* Luxury Gradient Background - Dark Theme */}
      {isDark ? (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800 pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }}></div>
          
          {/* Abstract Shapes - Dark */}
          <div className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.1) 0%, transparent 70%)' }}></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.15) 0%, transparent 70%)' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl pointer-events-none"></div>
          
          {/* Floating Abstract Lines - Dark */}
          <div className="absolute top-40 left-20 w-64 h-1 rounded-full transform rotate-45 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.3) 0%, transparent 100%)' }}></div>
          <div className="absolute bottom-40 right-20 w-48 h-1 rounded-full transform -rotate-12 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.2) 0%, transparent 100%)' }}></div>
        </>
      ) : (
        <>
          {/* Light Theme Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-gray-100 pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-amber-100/30 via-yellow-50/20 to-transparent pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.08) 0%, rgba(125, 102, 51, 0.04) 50%, transparent 100%)' }}></div>
          
          {/* Abstract Shapes - Light */}
          <div className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.06) 0%, transparent 70%)' }}></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.08) 0%, transparent 70%)' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gray-200/20 rounded-full blur-3xl pointer-events-none"></div>
          
          {/* Floating Abstract Lines - Light */}
          <div className="absolute top-40 left-20 w-64 h-1 rounded-full transform rotate-45 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.2) 0%, transparent 100%)' }}></div>
          <div className="absolute bottom-40 right-20 w-48 h-1 rounded-full transform -rotate-12 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.15) 0%, transparent 100%)' }}></div>
        </>
      )}
      
      {/* Content Container */}
      <div className={`relative z-10 h-full ${isDark ? 'text-white' : 'text-gray-900'} max-w-full overflow-x-hidden`}>
        {/* Only show topbar, sidebar, and bottom nav if authenticated */}
        {!isPublicBooking && (
          <PortalTopbar
            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
            isSidebarOpen={sidebarOpen}
          />
        )}
        
        <div className={`flex ${isPublicBooking ? '' : 'pt-24'} h-full`}>
          {!isPublicBooking && (
            <PortalSidebar 
              isOpen={sidebarOpen} 
              onClose={() => setSidebarOpen(false)} 
            />
          )}
          
          <main className={`flex-1 ${isPublicBooking ? '' : 'md:ml-64'} relative ${isPublicBooking ? 'pb-4' : 'pb-20 md:pb-4'} overflow-y-auto h-full`}>
            <div className={`px-4 md:px-6 lg:px-8 ${isPublicBooking ? 'py-4' : 'py-4 md:py-6 lg:py-8'} w-full max-w-full overflow-x-hidden`}>
              <div className="w-full max-w-full">
                {children}
              </div>
            </div>
          </main>
        </div>
        
        {!isPublicBooking && <PortalBottomNav />}
      </div>
    </div>
  );
};

export default PortalLayout;
